import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { AlertBox } from '@/common/AlertBox';
import { FormInput } from '@/common/forms/FormInput';
import { WBSButton } from '@/common/WBSButton';

export type LoginInfoField =
  | 'currentPassword'
  | 'accountEmail'
  | 'newPassword'
  | 'repeatPassword';

interface SettingsLoginInfoProps {
  formData: Record<LoginInfoField, string>;
  onInputChange: (field: LoginInfoField, value: string) => void;
  onSaveChanges: () => void;
  isSaving?: boolean;
}

export function SettingsLoginInfo({
  formData,
  onInputChange,
  onSaveChanges,
  isSaving,
}: SettingsLoginInfoProps) {
  const { t } = useTranslation();

  return (
    <FormContainer>
      {/* Legacy opens the screen with this line. */}
      <IntroParagraph>
        {t('notifications.update-your-email-and')}
      </IntroParagraph>
      <PasswordFieldGroup>
        <FormInput
          label={t('notifications.current-password-required-to')}
          type="password"
          value={formData.currentPassword}
          onChange={(value) => onInputChange('currentPassword', value)}
          autoComplete="current-password"
          alternate
        />
        {/* Legacy links to WordPress's lost-password page; this is the same
           screen the login page links to. */}
        <ForgotPasswordLink>
          <Link to="/login/" search={{ action: 'lostpassword' }}>
            {t('wbs.forgot-your-password')}
          </Link>
        </ForgotPasswordLink>
      </PasswordFieldGroup>
      <FormInput
        label={t('notifications.account-email')}
        type="email"
        value={formData.accountEmail}
        onChange={(value) => onInputChange('accountEmail', value)}
        autoComplete="email"
        alternate
      />

      <AlertBox variant="info">{t('wbs.leave-password-fields-blank')}</AlertBox>

      <FormInput
        label={t('actions.add-your-new-password')}
        type="password"
        value={formData.newPassword}
        onChange={(value) => onInputChange('newPassword', value)}
        autoComplete="new-password"
        alternate
      />
      <FormInput
        label={t('wbs.repeat-your-new-password')}
        type="password"
        value={formData.repeatPassword}
        onChange={(value) => onInputChange('repeatPassword', value)}
        autoComplete="new-password"
        alternate
      />
      <div style={{ marginBottom: 21 }}>
        <WBSButton wide onClick={onSaveChanges} isLoading={isSaving}>
          {t('actions.save-changes')}
        </WBSButton>
      </div>
    </FormContainer>
  );
}

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const IntroParagraph = styled.p`
  color: var(--grey-500);
  font-size: 16px;
  letter-spacing: -0.27px;
  margin: -6px 0 0 0;
`;

const PasswordFieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

// A raw `Link` inside a styled wrapper, because `styled(Link)` loses the
// router's route-aware typing.
const ForgotPasswordLink = styled.span`
  align-self: flex-start;

  a {
    color: var(--wbs-red);
    font-family: var(--open-sans);
    font-size: 16px;
    margin: 2px 0 0 4px;
    text-decoration: none;
  }
`;
